import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { Rect, Text, Group } from 'react-konva';
import { IFrame } from '../../core/frames/IFrame';
import { useTranslation } from '../../hooks/useTranslation';

interface FrameRendererProps {
  frame: IFrame;
  selected?: boolean;
  isDropTarget?: boolean;
  onSelect?: (frameId: string, e: any) => void;
  onDoubleClick?: (frameId: string, e: any) => void;
  onDragStart?: (frameId: string) => void;
  onDragMove?: (frameId: string, pos: { x: number; y: number }) => void;
  onDragEnd?: (frameId: string, pos: { x: number; y: number }) => void;
  onTransformStart?: (frameId: string, e: any) => void;
  onTransformEnd?: (frameId: string, e: any) => void;
  registerRef?: (frameId: string, node: any | null) => void;
  registerRectRef?: (frameId: string, node: any | null) => void;
  stageScale?: number;
}

const FrameRendererInner: React.FC<FrameRendererProps> = ({
  frame,
  selected = false,
  isDropTarget = false,
  onSelect,
  onDoubleClick,
  onDragStart,
  onDragMove,
  onDragEnd,
  onTransformStart,
  onTransformEnd,
  registerRef,
  registerRectRef,
  stageScale = 1
}) => {
  const { t } = useTranslation('genogram');
  const frameTitle = useMemo(() => {
    const raw = frame.name || '';
    const familyMatch = /^Family (\d+)$/.exec(raw);
    if (familyMatch) {
      return t('tools.defaultFamilyGroupName', { n: Number(familyMatch[1]), defaultValue: raw });
    }
    const genericMatch = /^Frame (\d+)$/.exec(raw);
    if (genericMatch) {
      return t('tools.defaultGenericFrameName', { n: Number(genericMatch[1]), defaultValue: raw });
    }
    return raw;
  }, [frame.name, t]);

  // Keep latest callbacks without changing handler identities
  const latest = useRef({ onSelect, onDoubleClick, onDragStart, onDragMove, onDragEnd, onTransformStart, onTransformEnd, registerRef, registerRectRef });
  useEffect(() => {
    latest.current = { onSelect, onDoubleClick, onDragStart, onDragMove, onDragEnd, onTransformStart, onTransformEnd, registerRef, registerRectRef };
  }, [onSelect, onDoubleClick, onDragStart, onDragMove, onDragEnd, onTransformStart, onTransformEnd, registerRef, registerRectRef]);

  const handleClick = useCallback((e: any) => {
    e.cancelBubble = true;
    latest.current.onSelect?.(frame.id, e);
  }, [frame.id]);

  const handleDoubleClick = useCallback((e: any) => {
    e.cancelBubble = true;
    latest.current.onDoubleClick?.(frame.id, e);
  }, [frame.id]);

  const handleDragStart = useCallback(() => {
    latest.current.onDragStart?.(frame.id);
  }, [frame.id]);

  const handleDragMove = useCallback((e: any) => {
    const pos = e.target.position();
    latest.current.onDragMove?.(frame.id, pos);
  }, [frame.id]);

  const handleDragEnd = useCallback((e: any) => {
    const pos = e.target.position();
    latest.current.onDragEnd?.(frame.id, pos);
  }, [frame.id]);

  const stableRegisterGroupRef = useCallback((n: any | null) => {
    latest.current.registerRef?.(frame.id, n);
  }, [frame.id]);
  const stableRegisterRectRef = useCallback((n: any | null) => {
    latest.current.registerRectRef?.(frame.id, n);
  }, [frame.id]);
  
  if (!frame.visible) return null;

  // Optional domain styling via frame.metadata. Domains (Compass AWS
  // drop, future ones) read these to get a branded frame without
  // requiring per-domain renderer registration.
  //   borderDash?: number[]          e.g. [8, 4] for AWS dashed look
  //   pillTone?: 'neutral' | 'aws-vpc' | 'aws-subnet' | 'aws-account'
  //   pillGlyph?: string             a 1-char glyph prepended in the pill
  const meta = (frame.metadata ?? {}) as Record<string, unknown>;
  const borderDash = Array.isArray(meta.borderDash)
    ? (meta.borderDash as number[])
    : undefined;
  const pillTone = (meta.pillTone as string | undefined) ?? 'neutral';
  const pillGlyph = typeof meta.pillGlyph === 'string' ? (meta.pillGlyph as string) : null;
  const tone = pillToneColors(pillTone, frame.borderColor);
  const effectiveBorder = tone.border;
  const effectiveBorderWidth = frame.borderWidth || (pillTone === 'neutral' ? 1 : 2);

  return (
    <Group
      x={frame.bounds.x}
      y={frame.bounds.y}
      draggable={!frame.locked}
      ref={stableRegisterGroupRef}
      name="frame-group"
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
      onClick={handleClick}
      onDblClick={handleDoubleClick}
      onDblTap={handleDoubleClick}
      onTransformStart={(e) => latest.current.onTransformStart?.(frame.id, e)}
      onTransformEnd={(e) => latest.current.onTransformEnd?.(frame.id, e)}
      opacity={frame.opacity || 1}
    >
      {/* Frame outline only */}
      <Rect
        ref={stableRegisterRectRef}
        width={frame.bounds.width}
        height={frame.bounds.height}
        fill={undefined}
        stroke={selected || isDropTarget ? '#9DB5FF' : effectiveBorder}
        strokeWidth={selected || isDropTarget ? 3 : effectiveBorderWidth}
        cornerRadius={8}
        dash={borderDash}
        onTransformStart={(e) => latest.current.onTransformStart?.(frame.id, e)}
        onTransformEnd={(e) => latest.current.onTransformEnd?.(frame.id, e)}
        name="frame-rect"
      />

      {/* Title pill outside top-left; keep screen size constant via inverse scaling */}
      <Group x={-4 / stageScale} y={-24 / stageScale} scaleX={1 / stageScale} scaleY={1 / stageScale} listening={false}>
        <Rect
          x={0}
          y={0}
          height={22}
          width={Math.max(72, frameTitle.length * 7 + (pillGlyph ? 30 : 18))}
          fill={tone.pillBg}
          stroke={tone.pillBorder}
          strokeWidth={1}
          cornerRadius={11}
          shadowForStrokeEnabled={false}
        />
        {pillGlyph ? (
          <>
            <Rect
              x={4}
              y={3}
              width={16}
              height={16}
              fill={tone.glyphBg}
              cornerRadius={4}
            />
            <Text
              x={4}
              y={3}
              width={16}
              height={16}
              text={pillGlyph}
              align="center"
              verticalAlign="middle"
              fontSize={11}
              fontStyle="bold"
              fontFamily="system-ui, -apple-system, sans-serif"
              fill={tone.glyphFg}
            />
          </>
        ) : null}
        <Text
          x={pillGlyph ? 24 : 8}
          y={4}
          text={frameTitle}
          fontSize={12}
          fontFamily="system-ui, -apple-system, sans-serif"
          fill={tone.pillFg}
        />
      </Group>

      {/* Frame type indicator removed */}
      {/* No custom handles; resizing handled via Konva Transformer in parent */}
    </Group>
  );
};

/**
 * AWS-leaning palette for compass drops. Neutral keeps the legacy
 * look for every non-AWS frame. Borders are the resource-group
 * outline colour; pill is the small label-pill at the top-left.
 */
function pillToneColors(
  tone: string,
  fallbackBorder: string | undefined,
): {
  border: string;
  pillBg: string;
  pillBorder: string;
  pillFg: string;
  glyphBg: string;
  glyphFg: string;
} {
  switch (tone) {
    case 'aws-vpc':
      return {
        border: '#7E3DDF',
        pillBg: '#FFFFFF',
        pillBorder: '#7E3DDF',
        pillFg: '#4B2A8A',
        glyphBg: '#7E3DDF',
        glyphFg: '#FFFFFF',
      };
    case 'aws-subnet-public':
      return {
        border: '#00A4A6',
        pillBg: '#FFFFFF',
        pillBorder: '#00A4A6',
        pillFg: '#035F61',
        glyphBg: '#00A4A6',
        glyphFg: '#FFFFFF',
      };
    case 'aws-subnet-private':
    case 'aws-subnet':
      return {
        border: '#248AED',
        pillBg: '#FFFFFF',
        pillBorder: '#248AED',
        pillFg: '#0B4E99',
        glyphBg: '#248AED',
        glyphFg: '#FFFFFF',
      };
    case 'aws-account':
      return {
        border: '#232F3E',
        pillBg: '#FFFFFF',
        pillBorder: '#232F3E',
        pillFg: '#232F3E',
        glyphBg: '#232F3E',
        glyphFg: '#FFFFFF',
      };
    default:
      return {
        border: fallbackBorder || '#E5E7EB',
        pillBg: '#FFFFFF',
        pillBorder: fallbackBorder || '#E5E7EB',
        pillFg: '#475569',
        glyphBg: '#E5E7EB',
        glyphFg: '#475569',
      };
  }
}

FrameRendererInner.displayName = 'FrameRenderer';

const areEqual = (prev: FrameRendererProps, next: FrameRendererProps) => {
  // Only re-render when frame identity, selection flags, or stageScale change
  // Ignore function props and ref callbacks to avoid churn
  return (
    prev.frame === next.frame &&
    prev.selected === next.selected &&
    prev.isDropTarget === next.isDropTarget &&
    (prev.stageScale ?? 1) === (next.stageScale ?? 1)
  );
};

export const FrameRenderer = memo(FrameRendererInner, areEqual);

// Component for rendering frame preview during creation
export const FramePreview: React.FC<{ bounds: { x: number; y: number; width: number; height: number } | null }> = memo(({ bounds }) => {
  if (!bounds || bounds.width < 1 || bounds.height < 1) return null;
  
  return (
    <Rect
      x={bounds.x}
      y={bounds.y}
      width={bounds.width}
      height={bounds.height}
      fill="rgba(156, 39, 176, 0.1)"
      stroke="#9c27b0"
      strokeWidth={2}
      cornerRadius={8}
      dash={[10, 5]}
    />
  );
}, (prev, next) => prev.bounds === next.bounds);
